import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowBox } from '~/components/common/arrow-box';
import { CompetitionCell, type CompetitionBand } from '~/components/common/competition-cell';
import { DeltaBadge } from '~/components/common/delta-badge';
import { ProductRowCard } from '~/components/common/product-row-card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';
import { formatCompactNumber } from '~/utils/formatting';

import {
  useInsightMembershipProducts,
  type InsightMembershipGroupFilter,
} from '../../hooks/use-insight-membership-products';
import type { InsightMembershipCategory } from '../../types/catalog-operations.api.types';
import type { BatchDetailTab } from '../../types/catalog-operations.types';

import { InsightMembershipExportButton } from './insight-membership-export-button';

/** Category-specific header stats. None of this rides on the membership
 *  response itself — it's captured from the already-fetched batch-insights
 *  row for this same label at the moment the caller opens the drawer, so
 *  opening it never costs a second request. `null` when the caller couldn't
 *  find a matching insights row (drawer still opens, just without the stat
 *  strip). */
export type MembershipHeaderStats =
  | {
      kind: 'term';
      monthlySearches: number;
      competition: CompetitionBand;
      countBefore: number;
      countAfter: number;
    }
  | {
      kind: 'occasion';
      coverageBefore: number;
      coverageAfter: number;
      countBefore: number;
      countAfter: number;
    }
  | {
      kind: 'trend';
      trendKind: 'macro' | 'micro';
      countBefore: number;
      countAfter: number;
    };

interface InsightMembershipDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  /** `null` between rows — the drawer stays mounted but fetches nothing
   *  until both are set. The caller renders this keyed on `category:label`
   *  (e.g. `key={\`${category}:${label}\`}`) so a different row fully
   *  remounts it and the group filter resets for free. */
  category: InsightMembershipCategory | null;
  label: string | null;
  headerStats: MembershipHeaderStats | null;
  /** Required for a scoped Popular Attribute row — that card publishes each
   *  attribute both catalog-wide and per category under different keys.
   *  Unused for Term/Occasion/Trend rows. */
  productCategory?: string;
  /** Job id from the batch route (not the workflow slug) — the product detail
   *  route keys on it. Rows become links to that product when set. */
  jobId?: string;
  /** Batch-detail tab to land back on from the product page. */
  returnTab?: BatchDetailTab;
  /** Chip to start on — set when the drawer is restored after a product visit. */
  initialGroup?: InsightMembershipGroupFilter;
}

/** Signs a delta for display — `+3`, `-3`, or `0`, never the mis-signed `+-3`
 *  a bare template-literal `+` prefix would produce for a negative value. */
function formatSignedDelta(delta: number): string {
  return delta > 0 ? `+${String(delta)}` : String(delta);
}

const CHIP_ORDER: { key: InsightMembershipGroupFilter; labelKey: string }[] = [
  { key: 'ALL', labelKey: 'chips.all' },
  { key: 'NEWLY_ADDED', labelKey: 'chips.newlyAdded' },
  { key: 'CURRENTLY_SHOWN', labelKey: 'chips.currentlyShown' },
  { key: 'REMOVED', labelKey: 'chips.removed' },
];

/** Stat strip for a Product Term row — unchanged from the drawer's original,
 *  dedicated design, just now fed by `headerStats` instead of a term-specific
 *  API response. */
function TermHeaderStats({ stats }: { stats: Extract<MembershipHeaderStats, { kind: 'term' }> }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div
      data-testid="batch-detail-membership-drawer-stats-term"
      className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-3 pt-1"
    >
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.monthlySearches')}</p>
        <p className="text-foreground text-sm font-semibold tabular-nums">
          {Number.isFinite(stats.monthlySearches)
            ? formatCompactNumber(stats.monthlySearches)
            : '—'}
        </p>
      </div>
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.competition')}</p>
        <CompetitionCell
          competition={stats.competition}
          testId="batch-detail-membership-drawer-competition"
        />
      </div>
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.productsWithTerm')}</p>
        <div className="flex items-center gap-2">
          <span className="text-warning text-sm font-semibold tabular-nums">
            {stats.countBefore}
          </span>
          <ArrowBox />
          <span className="text-primary text-sm font-semibold tabular-nums">
            {stats.countAfter}
          </span>
          <DeltaBadge
            delta={stats.countAfter - stats.countBefore}
            testId="batch-detail-membership-drawer-delta"
          />
        </div>
      </div>
    </div>
  );
}

/** Stat strip for an Occasion row: coverage %, products tagged, and the
 *  newly-tagged delta as plain text (no coloured chip — matches the design's
 *  denser, three-up layout). */
function OccasionHeaderStats({
  stats,
}: {
  stats: Extract<MembershipHeaderStats, { kind: 'occasion' }>;
}) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div
      data-testid="batch-detail-membership-drawer-stats-occasion"
      className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-3 pt-1"
    >
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.coverage')}</p>
        <div className="flex items-center gap-2">
          <span className="text-warning text-sm font-semibold tabular-nums">
            {stats.coverageBefore}%
          </span>
          <ArrowBox />
          <span className="text-primary text-sm font-semibold tabular-nums">
            {stats.coverageAfter}%
          </span>
        </div>
      </div>
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.productsTagged')}</p>
        <div className="flex items-center gap-2">
          <span className="text-warning text-sm font-semibold tabular-nums">
            {stats.countBefore}
          </span>
          <ArrowBox />
          <span className="text-primary text-sm font-semibold tabular-nums">
            {stats.countAfter}
          </span>
        </div>
      </div>
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.newlyTagged')}</p>
        <p className="text-foreground text-sm font-semibold tabular-nums">
          {formatSignedDelta(stats.countAfter - stats.countBefore)}
        </p>
      </div>
    </div>
  );
}

/** Stat strip for a Macro/Micro Trend row: products with the trend, the
 *  newly-tagged delta, and the signal type (macro vs micro). */
function TrendHeaderStats({ stats }: { stats: Extract<MembershipHeaderStats, { kind: 'trend' }> }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div
      data-testid="batch-detail-membership-drawer-stats-trend"
      className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-3 pt-1"
    >
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.productsWithTrend')}</p>
        <div className="flex items-center gap-2">
          <span className="text-warning text-sm font-semibold tabular-nums">
            {stats.countBefore}
          </span>
          <ArrowBox />
          <span className="text-primary text-sm font-semibold tabular-nums">
            {stats.countAfter}
          </span>
        </div>
      </div>
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.newlyTagged')}</p>
        <p className="text-foreground text-sm font-semibold tabular-nums">
          {formatSignedDelta(stats.countAfter - stats.countBefore)}
        </p>
      </div>
      <div>
        <p className="text-muted-foreground text-xs">{t('membershipDrawer.signalType')}</p>
        <p className="text-foreground text-sm font-semibold">
          {t(`membershipDrawer.signalTypeValue.${stats.trendKind}`)}
        </p>
      </div>
    </div>
  );
}

/** Eyebrow label above the title for Occasion/Trend rows — Term rows have
 *  none, matching the drawer's original, unlabelled title. */
function useEyebrow(stats: MembershipHeaderStats | null): string | null {
  const { t } = useTranslation('catalog-operations');
  if (!stats) return null;
  if (stats.kind === 'occasion') return t('membershipDrawer.eyebrow.occasion');
  if (stats.kind === 'trend') {
    return t(`membershipDrawer.eyebrow.${stats.trendKind}Trend`);
  }
  return null;
}

/**
 * Side drawer for a Product Term / Occasion / Popular Attribute / Macro or
 * Micro Trend row. Group chips built by **iterating `counts`' own keys**
 * (never a hardcoded list, so `POPULAR_ATTRIBUTE`'s absent `REMOVED`
 * disappears on its own), and the shared `ProductRowCard` list with infinite
 * scroll. Fetches lazily via `useInsightMembershipProducts`, gated on
 * `open && !!category && !!label`. The header's stat strip is category-
 * specific and driven entirely by `headerStats`, not by this response.
 */
export function InsightMembershipDrawer({
  open,
  onOpenChange,
  workflowId,
  category,
  label,
  headerStats,
  productCategory,
  jobId,
  returnTab,
  initialGroup = 'ALL',
}: InsightMembershipDrawerProps) {
  const { t } = useTranslation('catalog-operations');
  const [group, setGroup] = useState<InsightMembershipGroupFilter>(initialGroup);
  const eyebrow = useEyebrow(headerStats);

  const query = useInsightMembershipProducts(
    workflowId,
    category ?? 'OCCASION',
    label ?? '',
    group,
    open && !!category && !!label,
    productCategory,
  );
  const pages = query.data?.pages ?? [];
  const first = pages[0];
  const products = pages.flatMap((page) => page.products.content);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [group]);

  // Infinite-scroll sentinel — fetch the next page as it scrolls into view.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [products.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const counts = first?.counts;
  const chips = counts
    ? CHIP_ORDER.flatMap(({ key, labelKey }) => {
        const count = counts[key];
        return count == null ? [] : [{ key, label: t(`membershipDrawer.${labelKey}`), count }];
      })
    : [];
  const showChips = chips.length > 1;
  // Size of the list the footer exports — the selected chip's own count.
  const selectedCount = counts?.[group] ?? undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="batch-detail-membership-drawer"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b px-5 py-4 pr-12">
          {eyebrow && (
            <p
              data-testid="batch-detail-membership-drawer-eyebrow"
              className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
            >
              {eyebrow}
            </p>
          )}
          <SheetTitle
            data-testid="batch-detail-membership-drawer-title"
            className="truncate text-base"
          >
            {first?.label ?? label}
          </SheetTitle>
          {/* Term rows show this sentence visibly, matching the drawer's
           *  original design; Occasion/Trend rows have their eyebrow label
           *  instead and keep this only for screen readers. */}
          <SheetDescription className={eyebrow ? 'sr-only' : undefined}>
            {t('membershipDrawer.description')}
          </SheetDescription>
          {headerStats?.kind === 'term' && <TermHeaderStats stats={headerStats} />}
          {headerStats?.kind === 'occasion' && <OccasionHeaderStats stats={headerStats} />}
          {headerStats?.kind === 'trend' && <TrendHeaderStats stats={headerStats} />}
        </SheetHeader>

        {showChips && (
          <div
            data-testid="batch-detail-membership-drawer-chips"
            className="flex flex-wrap items-center gap-2 border-b px-5 py-3"
          >
            {chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                data-testid={`batch-detail-membership-drawer-chip-${chip.key}`}
                aria-pressed={group === chip.key}
                onClick={() => {
                  setGroup(chip.key);
                }}
                className={cn(
                  'flex shrink-0 cursor-pointer items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors',
                  group === chip.key
                    ? 'bg-foreground text-background border-foreground'
                    : 'text-muted-foreground hover:border-foreground/40 border-border',
                )}
              >
                {chip.label}
                <span
                  data-testid={`batch-detail-membership-drawer-chip-count-${chip.key}`}
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-bold',
                    group === chip.key ? 'bg-background/20' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {chip.count}
                </span>
              </button>
            ))}
          </div>
        )}

        <div ref={scrollRef} className="bg-muted/40 flex-1 overflow-y-auto px-5 py-4">
          {/* `isPlaceholderData` means we're showing the previous group's
           *  rows while the newly-selected chip's page loads — swap only
           *  this list for a skeleton rather than the whole panel, so the
           *  header and chips (built from the same stable counts) don't
           *  flicker out and back in. */}
          {query.isLoading || query.isPlaceholderData ? (
            <div
              data-testid="batch-detail-membership-drawer-loading"
              className="space-y-3"
              aria-busy="true"
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : query.isError ? (
            <p
              data-testid="batch-detail-membership-drawer-error"
              className="text-destructive text-sm"
            >
              {t('membershipDrawer.error')}
            </p>
          ) : products.length === 0 ? (
            <p
              data-testid="batch-detail-membership-drawer-empty"
              className="text-muted-foreground py-8 text-center text-sm"
            >
              {t('membershipDrawer.empty')}
            </p>
          ) : (
            <div data-testid="batch-detail-membership-drawer-list" className="space-y-2">
              {products.map((product) =>
                jobId && category && label ? (
                  // Opens the product's detail page; the search params carry
                  // this drawer (row + chip) so "Batch Details" re-opens it.
                  <Link
                    key={product.productId}
                    data-testid={`batch-detail-membership-drawer-product-link-${product.productId}`}
                    to="/catalog-operations/$batchId/products/$productId"
                    params={{ batchId: jobId, productId: product.productId }}
                    search={{
                      from: 'batch-detail',
                      ...(returnTab ? { returnTab } : {}),
                      membership: category,
                      membershipLabel: label,
                      membershipGroup: group,
                    }}
                    className="focus-visible:ring-ring block cursor-pointer rounded-lg focus-visible:ring-2 focus-visible:outline-none"
                  >
                    <ProductRowCard product={product} i18nPrefix="membershipDrawer" interactive />
                  </Link>
                ) : (
                  <ProductRowCard
                    key={product.productId}
                    product={product}
                    i18nPrefix="membershipDrawer"
                  />
                ),
              )}
              <div ref={loadMoreRef} className="flex items-center justify-center py-3">
                {isFetchingNextPage && <Skeleton className="h-16 w-full rounded-lg" />}
                {!hasNextPage && !isFetchingNextPage && first && (
                  <span
                    data-testid="batch-detail-membership-drawer-all-loaded"
                    className="text-muted-foreground text-xs"
                  >
                    {t('membershipDrawer.allLoaded', { count: first.products.totalElements })}
                  </span>
                )}
              </div>
            </div>
          )}

          {first?.note && (
            <p className="text-muted-foreground mt-4 border-t pt-3 text-xs leading-5">
              {first.note}
            </p>
          )}
        </div>

        {/* Footer — exports the whole list the drawer is filtered to (selected chip +
         *  product category) as CSV, not just the pages loaded so far. Hidden when the
         *  list itself failed: there's nothing trustworthy to export. */}
        {category && label && !query.isError && (
          <div
            data-testid="batch-detail-membership-drawer-footer"
            className="flex items-center justify-between gap-3 border-t bg-white px-5 py-3"
          >
            <span
              data-testid="batch-detail-membership-drawer-footer-count"
              className="text-muted-foreground text-xs tabular-nums"
            >
              {selectedCount === undefined
                ? null
                : t('membershipDrawer.export.footerCount', { count: selectedCount })}
            </span>
            <InsightMembershipExportButton
              workflowId={workflowId}
              category={category}
              label={label}
              group={group}
              {...(productCategory ? { productCategory } : {})}
              count={selectedCount}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
